import React from 'react';

import { CurationActions, CurationContext } from '@contexts/index';

import type { FhirResource } from '@models/fhir-resource';

import { useUpdateCurationStatusMutation } from 'generated/curation-meta.generated';
import type { CurationStatus as Status } from 'generated/types';
import { CurationStatus } from 'generated/types';
import { useGetVitallPatientRecordLazyQuery } from 'generated/vitall-patient.generated';

import { useSession } from './useSession';

export function useCuration() {
  const context = React.useContext(CurationContext);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (context === undefined) {
    throw new Error(`useCuration must be used within a CurationProvider`);
  }

  const { state, dispatch } = context;
  const { resetActiveProfile, setActiveProfile } = useSession();

  const [updateCurationStatus] = useUpdateCurationStatusMutation();
  const [getVitallPatient] = useGetVitallPatientRecordLazyQuery();

  const startCurating = (record: FhirResource | null) => {
    dispatch({
      type: CurationActions.StartCuration,
      record,
    });
  };

  const stopCurating = () => {
    dispatch({ type: CurationActions.StopCuration });
  };

  const viewAsPatient = async (user_id: string) => {
    const response = await getVitallPatient({
      variables: {
        input: { user_id },
      },
    });
    const vitall_patient = response.data?.getVitallPatientRecord;
    setActiveProfile(user_id);
    if (vitall_patient?.vitall_patient_id) {
      dispatch({
        type: CurationActions.View,
        vitall_patient_id: vitall_patient.vitall_patient_id,
      });
    }
  };

  const reset = async () => {
    resetActiveProfile();
    dispatch({ type: CurationActions.Reset });
  };

  const startEditingRecord = () => {
    dispatch({ type: CurationActions.StartEdit });
  };

  const stopEditingRecord = () => {
    dispatch({ type: CurationActions.StopEdit });
  };

  const startAddingRecord = () => {
    dispatch({ type: CurationActions.StartCreating });
  };

  const finishAddingRecord = () => {
    dispatch({ type: CurationActions.FinishCreating });
  };

  const setRecord = (record: FhirResource | null) => {
    dispatch({ type: CurationActions.SetRecord, record });
  };

  const updateRecordCurationStatus = async (status: CurationStatus) => {
    const { record } = state;
    // Published is final state for record, don't update
    if (!record || record.curationStatus === CurationStatus.Published) {
      return;
    }
    const response = await updateCurationStatus({
      variables: {
        input: {
          resource: record.getResourceId(),
          status,
        },
      },
    });
    const { data } = response;
    const recordResponse = data?.updateCurationStatus;
    if (recordResponse) {
      const { curationMeta } = recordResponse;
      record.curationMeta = curationMeta;
      record.curationStatus = curationMeta?.status as Status;
    }
  };

  return {
    state,
    dispatch,
    reset,
    startCurating,
    stopCurating,
    startEditingRecord,
    stopEditingRecord,
    startAddingRecord,
    finishAddingRecord,
    setRecord,
    updateRecordCurationStatus,
    viewAsPatient,
  };
}
